import React from 'react';
import { Save, Eye, EyeOff, Globe, Trash2 } from 'lucide-react';

const INPUT_CLS = 'w-full border border-earth/15 rounded px-3 py-2 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-saffron/30';

export function SEOEditForm({ form, setForm, saving, onSave, onCancel, isNew }) {
  const bind = (key) => ({ value: form[key], onChange: (e) => setForm(f => ({ ...f, [key]: e.target.value })) });
  return (
    <div className="space-y-3">
      {isNew ? (
        <input {...bind('path')} placeholder="Page path (e.g., /turmeric-powder-exporter)" className={INPUT_CLS} autoFocus data-testid="seo-new-path" />
      ) : (
        <div className="flex items-center justify-between">
          <span className="text-xs font-sans font-semibold text-saffron">{form.path}</span>
          <button onClick={onCancel} className="text-earth/30 text-xs font-sans hover:text-earth" data-testid="seo-cancel">Cancel</button>
        </div>
      )}
      <input {...bind('meta_title')} placeholder="Meta Title (60 chars max)" className={INPUT_CLS} data-testid="seo-meta-title" />
      <textarea {...bind('meta_description')} placeholder="Meta Description (160 chars max)" rows={2} className={`${INPUT_CLS} resize-none`} data-testid="seo-meta-desc" />
      <input {...bind('keywords')} placeholder="Keywords (comma separated)" className={INPUT_CLS} data-testid="seo-keywords" />
      {!isNew && (
        <div className="grid grid-cols-2 gap-3">
          <input {...bind('canonical_url')} placeholder="Canonical URL (optional)" className={INPUT_CLS} data-testid="seo-canonical" />
          <label className="flex items-center gap-2 text-sm font-sans text-earth/60 cursor-pointer">
            <input type="checkbox" checked={form.noindex} onChange={e => setForm(f => ({ ...f, noindex: e.target.checked }))} className="accent-saffron" data-testid="seo-noindex" />
            {form.noindex ? <EyeOff size={12} /> : <Eye size={12} />} {form.noindex ? 'Noindex (hidden from Google)' : 'Indexed'}
          </label>
        </div>
      )}
      <div className="flex gap-2 items-center">
        <button onClick={onSave} disabled={saving} className="bg-saffron text-white font-sans font-semibold text-xs px-4 py-2 rounded-md hover:bg-saffron-dark flex items-center gap-1 disabled:opacity-50" data-testid={isNew ? 'save-seo-new' : 'save-seo'}>
          <Save size={12} /> {saving ? 'Saving...' : 'Save'}
        </button>
        {isNew && <button onClick={onCancel} className="text-xs font-sans text-earth/40 hover:text-earth" data-testid="seo-new-cancel">Cancel</button>}
      </div>
    </div>
  );
}

export function SEOPageSummary({ path, label, setting, isDefault, onEdit, onDelete }) {
  const slug = path.replace(/\//g, '-');
  return (
    <div className="flex items-start justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Globe size={12} className="text-saffron flex-shrink-0" />
          <span className="text-sm font-sans font-semibold text-earth-dark">{label || path}</span>
          <span className="text-[10px] text-earth/30 font-sans">{path}</span>
          {setting?.noindex && <span className="text-[9px] bg-red-100 text-red-500 px-1.5 py-0.5 rounded font-sans font-semibold">NOINDEX</span>}
        </div>
        {setting ? (
          <div className="mt-1">
            <p className="text-xs text-earth/60 font-sans truncate">{setting.meta_title || '(no title set)'}</p>
            <p className="text-[10px] text-earth/40 font-sans truncate mt-0.5">{setting.meta_description || '(no description set)'}</p>
          </div>
        ) : (
          <p className="text-[10px] text-earth/30 font-sans mt-1 italic">Using default SEO settings</p>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 ml-3">
        <button onClick={onEdit} className="text-[10px] font-sans text-saffron font-semibold hover:text-saffron-dark px-2 py-1 rounded hover:bg-saffron/5" data-testid={`edit-seo-${slug}`}>
          {setting ? 'Edit' : 'Set SEO'}
        </button>
        {setting && !isDefault && <button onClick={onDelete} className="text-red-400 hover:text-red-600 px-1" data-testid={`delete-seo-${slug}`}><Trash2 size={11} /></button>}
      </div>
    </div>
  );
}
